/**
 * Adapted from PlayCanvas Engine's gsplat-compressed-resource.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import * as THREE from 'three'
import type { GSplatCompressedData } from '@/plugins/gsplat/core/GSplatCompressedData'
import type {
  GSplatGraphicsAdapter,
  GSplatMaterialLeaf,
  GSplatTextureLeaf,
  GSplatTextureUsage,
} from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import { GSplatFormat, type GSplatStreamDescriptor } from '@/plugins/gsplat/lod/GSplatFormat'
import { GSplatStreams } from '@/plugins/gsplat/lod/GSplatStreams'
import {
  GSplatResourceBase,
  type GSplatResourceBaseOptions,
  type GSplatResourceDataSource,
} from '@/plugins/gsplat/unified/resource/GSplatResourceBase'

const READ_GLSL = '#include "gsplatCompressedVS"'
const READ_WGSL = '#include "gsplatCompressedVS"'
const CHUNK_TEXTURE_USAGES = Object.freeze([
  'sampled',
  'copy-src',
  'copy-dst',
] as const satisfies readonly GSplatTextureUsage[])

type SplatShapeReader = NonNullable<
  GSplatResourceBaseOptions<GSplatCompressedData, unknown>['readSplatShape']
>
type SplatColorReader = NonNullable<
  GSplatResourceBaseOptions<GSplatCompressedData, unknown>['readSplatColor']
>

export interface GSplatPackedStream {
  readonly name: string
  readonly data: ArrayBufferView
}

export interface GSplatCompressedResourceOptions<TData extends GSplatResourceDataSource, TIterator>
  extends GSplatResourceBaseOptions<TData, TIterator> {
  readonly chunks: Float32Array
  readonly packedStreams: readonly GSplatPackedStream[]
  readonly chunkTexture?: GSplatTextureLeaf | null
}

export type GSplatCompressedResourceCreateOptions = Omit<
  GSplatCompressedResourceOptions<GSplatCompressedData, unknown>,
  'adapter' | 'data' | 'format' | 'chunks' | 'packedStreams' | 'chunkTexture'
>

interface PackedCompressedData {
  readonly chunks: Float32Array
  readonly chunkTextureData: Float32Array
  readonly streams: readonly GSplatPackedStream[]
}

function createFormat(adapter: GSplatGraphicsAdapter, hasSH: boolean): GSplatFormat {
  const streams: GSplatStreamDescriptor[] = [{ name: 'packedTexture', format: 'rgba32uint' }]
  if (hasSH) {
    streams.push({ name: 'shTexture0', format: 'rgba32uint' })
    streams.push({ name: 'shTexture1', format: 'rgba32uint' })
    streams.push({ name: 'shTexture2', format: 'rgba32uint' })
  }
  return new GSplatFormat(adapter, streams, {
    readGLSL: READ_GLSL,
    readWGSL: READ_WGSL,
  })
}

function evalChunkTextureSize(
  adapter: GSplatGraphicsAdapter,
  numChunks: number
): Readonly<{ width: number; height: number }> {
  // GPU texture 不允许 0 尺寸；空资源保留一个全零的 5x1 padding row。
  const logicalWidth = Math.max(1, Math.ceil(Math.sqrt(numChunks)))
  const width = logicalWidth * 5
  const height = Math.max(1, Math.ceil(numChunks / logicalWidth))
  if (
    !Number.isSafeInteger(width) ||
    !Number.isSafeInteger(height) ||
    width > adapter.limits.maxTextureDimension2D ||
    height > adapter.limits.maxTextureDimension2D
  ) {
    throw new Error(
      `GSplatCompressedResource: chunk texture 超出 device limit，size=${width}x${height}, limit=${adapter.limits.maxTextureDimension2D}`
    )
  }
  return Object.freeze({ width, height })
}

function toAlignedUint32(data: Uint8Array, name: string): Uint32Array {
  if (data.byteOffset % Uint32Array.BYTES_PER_ELEMENT !== 0) {
    throw new Error(
      `GSplatCompressedResource: ${name} byteOffset 未按 4 字节对齐，actual=${data.byteOffset}`
    )
  }
  if (data.byteLength % Uint32Array.BYTES_PER_ELEMENT !== 0) {
    throw new Error(
      `GSplatCompressedResource: ${name} byteLength 未按 4 字节对齐，actual=${data.byteLength}`
    )
  }
  return new Uint32Array(
    data.buffer,
    data.byteOffset,
    data.byteLength / Uint32Array.BYTES_PER_ELEMENT
  )
}

function packCompressedData(
  data: GSplatCompressedData,
  splatCapacity: number,
  chunkWidth: number,
  chunkHeight: number
): PackedCompressedData {
  if (!Number.isSafeInteger(splatCapacity) || splatCapacity < data.numSplats) {
    throw new Error(
      `GSplatCompressedResource: texture capacity 不足，capacity=${splatCapacity}, numSplats=${data.numSplats}`
    )
  }
  const packedTexture = new Uint32Array(splatCapacity * 4)
  packedTexture.set(data.vertexData)
  const streams: GSplatPackedStream[] = [{ name: 'packedTexture', data: packedTexture }]
  if (data.shBands > 0) {
    const sources = [data.shData0!, data.shData1!, data.shData2!]
    for (let channel = 0; channel < sources.length; channel++) {
      const source = toAlignedUint32(sources[channel], `shData${channel}`)
      const target = new Uint32Array(splatCapacity * 4)
      target.set(source)
      streams.push({ name: `shTexture${channel}`, data: target })
    }
  }

  const chunks = new Float32Array(data.numChunks * 6)
  data.getChunks(chunks)
  const chunkTextureData = new Float32Array(chunkWidth * chunkHeight * 4)
  for (let chunk = 0; chunk < data.numChunks; chunk++) {
    const sourceOffset = chunk * data.chunkSize
    const targetOffset = chunk * 20
    for (let component = 0; component < data.chunkSize; component++) {
      chunkTextureData[targetOffset + component] = data.chunkData[sourceOffset + component]
    }
    if (data.chunkSize === 12) {
      chunkTextureData[targetOffset + 15] = 1
      chunkTextureData[targetOffset + 16] = 1
      chunkTextureData[targetOffset + 17] = 1
    }
  }
  return {
    chunks,
    chunkTextureData,
    streams: Object.freeze(streams.map((stream) => Object.freeze(stream))),
  }
}

function createShapeReader(data: GSplatCompressedData): SplatShapeReader {
  const position = new THREE.Vector3()
  const rotation = new THREE.Quaternion()
  const logScale = new THREE.Vector3()
  const iterator = data.createIter(position, rotation, logScale)
  return (index, targetPosition, targetRotation, targetRadii) => {
    if (!Number.isSafeInteger(index) || index < 0 || index >= data.numSplats) return false
    iterator.read(index)
    const rx = Math.exp(logScale.x)
    const ry = Math.exp(logScale.y)
    const rz = Math.exp(logScale.z)
    if (
      !Number.isFinite(position.x) ||
      !Number.isFinite(position.y) ||
      !Number.isFinite(position.z) ||
      !Number.isFinite(rx) ||
      !Number.isFinite(ry) ||
      !Number.isFinite(rz) ||
      rx <= 0 ||
      ry <= 0 ||
      rz <= 0
    ) {
      return false
    }
    targetPosition.copy(position)
    targetRotation.copy(rotation).normalize()
    if (targetRotation.w < 0) {
      targetRotation.set(-targetRotation.x, -targetRotation.y, -targetRotation.z, -targetRotation.w)
    }
    targetRadii.set(rx, ry, rz)
    return true
  }
}

function createColorReader(data: GSplatCompressedData): SplatColorReader {
  const color = new THREE.Vector4()
  const iterator = data.createIter(undefined, undefined, undefined, color)
  return (index, targetColor) => {
    if (!Number.isSafeInteger(index) || index < 0 || index >= data.numSplats) return false
    iterator.read(index)
    if (!Number.isFinite(color.x) || !Number.isFinite(color.y) || !Number.isFinite(color.z)) {
      return false
    }
    targetColor.copy(color)
    return true
  }
}

function createChunkTexture(
  adapter: GSplatGraphicsAdapter,
  width: number,
  height: number
): GSplatTextureLeaf {
  return adapter.createTexture(createChunkTextureDescriptor(width, height))
}

function createChunkTextureDescriptor(width: number, height: number) {
  return {
    label: 'chunkTexture',
    width,
    height,
    format: 'rgba32float' as const,
    usages: CHUNK_TEXTURE_USAGES,
  }
}

/** Compressed PLY 的 packed CPU stream/iterator owner。 */
export class GSplatCompressedResource<
  TData extends GSplatResourceDataSource = GSplatCompressedData,
  TIterator = unknown,
> extends GSplatResourceBase<TData, TIterator> {
  readonly kind = 'compressed' as const
  readonly chunks: Float32Array
  readonly packedStreams: readonly GSplatPackedStream[]
  readonly chunkTexture: GSplatTextureLeaf | null
  readonly shBands: number

  constructor(options: GSplatCompressedResourceOptions<TData, TIterator>) {
    super(options)
    const names = new Set<string>()
    for (const stream of options.packedStreams) {
      if (names.has(stream.name)) throw new Error(`GSplat packed stream 重复: ${stream.name}`)
      if (!options.format.getStream(stream.name)) {
        throw new Error(`GSplat packed stream 未在 format 中声明: ${stream.name}`)
      }
      names.add(stream.name)
    }
    const shBands = (options.data as { readonly shBands?: unknown }).shBands ?? 0
    if (!Number.isSafeInteger(shBands) || Number(shBands) < 0 || Number(shBands) > 3) {
      throw new Error(`GSplatCompressedResource: 非法 shBands=${String(shBands)}`)
    }
    this.shBands = Number(shBands)
    this.chunks = options.chunks
    this.packedStreams = Object.freeze(
      options.packedStreams.map((stream) => Object.freeze({ ...stream }))
    )
    this.chunkTexture = options.chunkTexture ?? null
  }

  static create(
    adapter: GSplatGraphicsAdapter,
    data: GSplatCompressedData,
    options: GSplatCompressedResourceCreateOptions = {}
  ): GSplatCompressedResource<GSplatCompressedData> {
    const hasSH = data.shBands > 0
    const format = createFormat(adapter, hasSH)
    const splatDimensions = adapter.calculateTextureDimensions(data.numSplats)
    const chunkDimensions = evalChunkTextureSize(adapter, data.numChunks)
    const packed = packCompressedData(
      data,
      splatDimensions.width * splatDimensions.height,
      chunkDimensions.width,
      chunkDimensions.height
    )
    const streams = new GSplatStreams(adapter)
    let chunkTexture: GSplatTextureLeaf | null = null
    try {
      // packed streams 与 chunk texture 必须全部先通过，不能在末尾 chunk 失败时留下前序分配。
      streams.preflight(format, data.numSplats)
      adapter.validateTextureDescriptor(
        createChunkTextureDescriptor(chunkDimensions.width, chunkDimensions.height)
      )
      streams.init(format, data.numSplats)
      chunkTexture = createChunkTexture(adapter, chunkDimensions.width, chunkDimensions.height)
      for (const stream of packed.streams) {
        const texture = streams.getTexture(stream.name)
        if (!texture)
          throw new Error(`GSplatCompressedResource: texture staging 缺失 ${stream.name}`)
        adapter.updateTexture(texture, stream.data)
      }
      adapter.updateTexture(chunkTexture, packed.chunkTextureData)
      const resource = new GSplatCompressedResource<GSplatCompressedData>({
        ...options,
        adapter,
        data,
        format,
        chunks: packed.chunks,
        packedStreams: packed.streams,
        chunkTexture,
        readSplatShape: options.readSplatShape ?? createShapeReader(data),
        readSplatColor: options.readSplatColor ?? createColorReader(data),
      })
      resource.attachStreams(streams)
      return resource
    } catch (error) {
      chunkTexture?.dispose()
      streams.destroy()
      throw error
    }
  }

  override get sourceGpuBytes(): number {
    if (!this.chunkTexture) return super.sourceGpuBytes
    const bytesPerTexel = this.adapter.getTextureFormatInfo(this.chunkTexture.format).bytesPerTexel
    return super.sourceGpuBytes + this.chunkTexture.width * this.chunkTexture.height * bytesPerTexel
  }

  override getTexture(name: string): GSplatTextureLeaf | null {
    this.assertActive()
    if (name === 'chunkTexture') return this.chunkTexture
    return super.getTexture(name)
  }

  /**
   * `chunkTexture` 不属于 format.resourceStreams，但 compressed reader 的
   * `gsplatCompressedVS` 会直接读取它；统一 Resource 配置入口必须补充这个
   * 手工绑定，否则 WorkBuffer source-copy 无法识别 compressed source。
   */
  override configureMaterial(
    material: GSplatMaterialLeaf,
    workBufferModifier: { readonly code: string; readonly hash: number } | null,
    formatDeclarations: string
  ): void {
    super.configureMaterial(material, workBufferModifier, formatDeclarations)
    if (!this.chunkTexture) throw new Error('GSplatCompressedResource 缺少 chunkTexture')
    material.setParameter('chunkTexture', this.chunkTexture)
  }

  override configureMaterialDefines(defines: Map<string, unknown>): void {
    super.configureMaterialDefines(defines)
    defines.set('SH_BANDS', this.shBands > 0 ? 3 : 0)
  }

  protected override actualDestroy(): void {
    if (this.isDestroyed) return
    this.chunkTexture?.dispose()
    super.actualDestroy()
  }
}
